import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft, FilePlus2, Link, Pencil, Save, ScanSearch, Send } from 'lucide-react';
import Swal from 'sweetalert2';
import Loading from '../../components/Loading/Loading';
import { Toast } from '../../helpers/alert';
import usePendingChanges from '../plan3d/usePendingChanges';
import { createCalculationModelsApi } from './api';
import CalculationEditor from './CalculationEditor';
import { backendGraphToFlow, flowGraphToBackend } from './model';
import { OPTION_LABELS } from './nodePresentationRegistry';
import './calculationModels.css';

const fingerprint = ({ nodes, edges, timezone, frequency }) => JSON.stringify({
  nodes: nodes.map(node => ({ id: node.id, type: node.type, position: node.position, data: { nodeType: node.data.nodeType, name: node.data.name, config: node.data.config } })),
  edges: edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    sourceHandle: edge.sourceHandle,
    target: edge.target,
    targetHandle: edge.targetHandle,
    data: { inputOrder: edge.data?.inputOrder ?? 0 },
  })),
  timezone,
  frequency,
});

const validationFromFailure = error => {
  const details = error.details;
  if (details && Array.isArray(details.errors)) return { mode: details.mode || 'structural', valid: false, errors: details.errors, warnings: details.warnings || [] };
  if (Array.isArray(details)) return {
    mode: 'structural', valid: false,
    errors: details.map(item => ({ code: 'INVALID_PAYLOAD', message: item.msg || 'Payload inválido.', node_key: item.path?.match(/^nodes\.(\d+)/) ? null : undefined })),
    warnings: [],
  };
  return null;
};

const activeDraftIdFrom = model => {
  const draft = (model?.versions || []).find(item => item?.status === 'draft');
  return draft?.id ?? draft?.version_id ?? null;
};

const draftFailureMessage = error => {
  if (error.status === 403) return 'No tenés permisos para crear o continuar este borrador.';
  if (error.status === 404) return 'No se encontró el modelo de cálculo solicitado.';
  return error.message || 'No se pudo resolver el borrador. Revisá tu conexión e intentá nuevamente.';
};

export function CalculationModelWorkspace({
  modelId: providedModelId,
  versionId: providedVersionId,
  projectContext = false,
  canWrite = true,
  onBack,
  backLabel = 'Volver al proyecto',
  onEditRequested,
  onPublished,
  onDirtyChange,
  sourceScope,
}) {
  const routeParams = useParams();
  const modelId = providedModelId || routeParams.modelId;
  const versionId = providedVersionId || routeParams.versionId;
  const navigate = useNavigate();
  const token = useSelector(state => state.user.currentUser?.accessToken);
  const authenticated = Boolean(token);
  const api = useMemo(() => createCalculationModelsApi(token), [token]);
  const apiRef = useRef(api);
  const sourceCatalogs = useMemo(() => ({
    branches: () => apiRef.current.branches(),
    devices: () => apiRef.current.devices(),
    basicParameters: deviceId => apiRef.current.basicParameters(deviceId),
    calculatedParameters: deviceId => apiRef.current.calculatedParameters(deviceId),
  }), []);
  const mounted = useRef(true);
  const draftRequestInFlight = useRef(false);
  const [catalog, setCatalog] = useState(null);
  const [model, setModel] = useState(null);
  const [version, setVersion] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [timezone, setTimezone] = useState('America/Argentina/Buenos_Aires');
  const [frequency, setFrequency] = useState('');
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const [validation, setValidation] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [resolvingDraft, setResolvingDraft] = useState(false);
  const [activeDraftVersionId, setActiveDraftVersionId] = useState(null);
  const [pendingAssociationVersion, setPendingAssociationVersion] = useState(null);
  const [loadError, setLoadError] = useState('');

  const currentFingerprint = useMemo(() => fingerprint({ nodes, edges, timezone, frequency }), [nodes, edges, timezone, frequency]);
  const dirty = Boolean(savedFingerprint) && currentFingerprint !== savedFingerprint;
  const readOnly = version?.status !== 'draft' || !canWrite;
  usePendingChanges(dirty);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => { apiRef.current = api; }, [api]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const applyVersion = useCallback(data => {
    const graph = backendGraphToFlow(data);
    const nextTimezone = data.default_timezone || 'America/Argentina/Buenos_Aires';
    const nextFrequency = data.default_frequency || '';
    setVersion(data);
    setNodes(graph.nodes);
    setEdges(graph.edges);
    setTimezone(nextTimezone);
    setFrequency(nextFrequency);
    setSelectedNodeId(null);
    setSavedFingerprint(fingerprint({ nodes: graph.nodes, edges: graph.edges, timezone: nextTimezone, frequency: nextFrequency }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) return;
      setLoading(true); setLoadError('');
      try {
        const [catalogData, versionData, modelData] = await Promise.all([
          apiRef.current.nodeTypes(),
          apiRef.current.version(modelId, versionId),
          apiRef.current.model(modelId),
        ]);
        if (cancelled || !mounted.current) return;
        setCatalog(catalogData);
        setModel(modelData);
        applyVersion(versionData);
        setActiveDraftVersionId(versionData.status === 'published' ? activeDraftIdFrom(modelData) : null);
      } catch (error) {
        if (!cancelled && mounted.current) setLoadError(error.message);
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // La renovación del token sólo actualiza apiRef; no debe reinicializar el grafo local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, modelId, versionId, authenticated]);

  const goToDraft = draftVersionId => {
    if (!draftVersionId) return;
    navigate(`/calculation-models/${modelId}/versions/${draftVersionId}`);
  };

  const resolveExistingDraft = async () => {
    const modelData = await apiRef.current.model(modelId);
    const draftVersionId = activeDraftIdFrom(modelData);
    setActiveDraftVersionId(draftVersionId);
    return draftVersionId;
  };

  const createOrContinueDraft = async () => {
    if (draftRequestInFlight.current || resolvingDraft) return;
    if (activeDraftVersionId) {
      goToDraft(activeDraftVersionId);
      return;
    }

    draftRequestInFlight.current = true;
    setResolvingDraft(true);
    try {
      const draft = await apiRef.current.createDraft(modelId);
      if (!mounted.current) return;
      const newVersionId = draft?.id ?? draft?.version_id;
      if (!newVersionId) throw new Error('El servidor no informó la versión del nuevo borrador.');
      setActiveDraftVersionId(newVersionId);
      Toast.fire({ icon: 'success', title: 'Nuevo borrador creado' });
      goToDraft(newVersionId);
    } catch (error) {
      if (!mounted.current) return;
      if (error.status === 409) {
        try {
          const draftVersionId = await resolveExistingDraft();
          if (!draftVersionId || !mounted.current) {
            Toast.fire({ icon: 'error', title: 'No se pudo localizar el borrador activo' });
            return;
          }
          const confirmation = await Swal.fire({
            title: 'Ya existe un borrador',
            text: 'Podés continuar editando el borrador activo de este modelo.',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Continuar borrador',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d39000',
          });
          if (confirmation.isConfirmed && mounted.current) goToDraft(draftVersionId);
        } catch (lookupError) {
          if (mounted.current) Toast.fire({ icon: 'error', title: draftFailureMessage(lookupError) });
        }
      } else {
        Toast.fire({ icon: 'error', title: draftFailureMessage(error) });
      }
    } finally {
      draftRequestInFlight.current = false;
      if (mounted.current) setResolvingDraft(false);
    }
  };

  const persistDraft = async ({ quiet = false } = {}) => {
    try {
      const payload = flowGraphToBackend({ nodes, edges, defaultTimezone: timezone, defaultFrequency: frequency });
      const saved = await apiRef.current.saveDraft(modelId, versionId, payload);
      if (!mounted.current) return saved;
      applyVersion(saved);
      setValidation(null);
      if (!quiet) Toast.fire({ icon: 'success', title: 'Borrador guardado' });
      return saved;
    } catch (error) {
      if (mounted.current) {
        const structural = validationFromFailure(error);
        if (structural) setValidation(structural);
        Toast.fire({ icon: 'error', title: error.message });
      }
      return null;
    }
  };

  const save = async () => {
    if (readOnly || busy) return;
    setBusy(true);
    try { await persistDraft(); }
    finally { if (mounted.current) setBusy(false); }
  };

  const focusFirstError = result => {
    const first = (result?.errors || []).find(error => error.node_key);
    if (!first) return;
    setSelectedNodeId(first.node_key);
    setFocusRequest(previous => ({ nodeId: first.node_key, sequence: Number(previous?.sequence || 0) + 1 }));
  };

  const validate = async () => {
    if (readOnly || busy) return;
    setBusy(true);
    try {
      if (dirty) {
        const saved = await persistDraft({ quiet: true });
        if (!saved || !mounted.current) return;
      }
      const result = await apiRef.current.validate(modelId, versionId);
      if (!mounted.current) return;
      setValidation(result);
      if (!result.valid) focusFirstError(result);
      Toast.fire({ icon: result.valid ? 'success' : 'warning', title: result.valid ? 'Modelo válido' : 'El modelo todavía está incompleto' });
    } catch (error) {
      if (mounted.current) {
        const structural = validationFromFailure(error);
        if (structural) setValidation(structural);
        Toast.fire({ icon: 'error', title: error.message });
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const publish = async () => {
    if (readOnly || busy) return;
    setBusy(true);
    try {
      if (dirty) {
        const saved = await persistDraft({ quiet: true });
        if (!saved || !mounted.current) return;
      }

      const result = await apiRef.current.validate(modelId, versionId);
      if (!mounted.current) return;
      setValidation(result);
      if (!result.valid) {
        focusFirstError(result);
        Toast.fire({ icon: 'warning', title: 'Revisá los errores antes de publicar' });
        return;
      }

      const confirmation = await Swal.fire({
        title: '¿Publicar esta versión?',
        text: 'Una versión publicada queda inmutable. Para modificarla después será necesario crear un nuevo borrador.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Publicar versión',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d39000',
        reverseButtons: true,
        focusCancel: true,
      });
      if (!confirmation.isConfirmed || !mounted.current) return;

      const published = await apiRef.current.publish(modelId, versionId);
      if (!mounted.current) return;
      applyVersion(published);
      setActiveDraftVersionId(null);
      setValidation(result);
      if (onPublished) {
        try {
          await onPublished(published, model);
          if (!mounted.current) return;
          setPendingAssociationVersion(null);
          Toast.fire({ icon: 'success', title: projectContext ? 'Cambios publicados' : 'Versión publicada' });
        } catch (_) {
          if (!mounted.current) return;
          setPendingAssociationVersion(published);
          Toast.fire({ icon: 'error', title: 'El cálculo fue publicado, pero no pudo asociarse al proyecto.' });
        }
      } else {
        Toast.fire({ icon: 'success', title: 'Versión publicada' });
      }
    } catch (error) {
      if (mounted.current) {
        const responseValidation = validationFromFailure(error);
        if (responseValidation) {
          setValidation(responseValidation);
          focusFirstError(responseValidation);
        }
        Toast.fire({ icon: 'error', title: error.message });
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const retryAssociation = async () => {
    if (!pendingAssociationVersion || !onPublished || busy) return;
    setBusy(true);
    try {
      await onPublished(pendingAssociationVersion, model);
      if (!mounted.current) return;
      setPendingAssociationVersion(null);
      Toast.fire({ icon: 'success', title: 'Cálculo asociado al proyecto' });
    } catch (_) {
      if (mounted.current) Toast.fire({ icon: 'error', title: 'No se pudo asociar el cálculo al proyecto.' });
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (loading) return <Loading />;
  if (loadError) return <div className="cm-load-error"><h1>No se pudo abrir el editor</h1><p>{loadError}</p></div>;

  return <div className="cm-page">
    {onBack && <button type="button" className="cm-back-button" onClick={onBack}><ArrowLeft size={16} />{backLabel}</button>}
    <header className="cm-toolbar">
      <div><span className="cm-eyebrow">MODELO DE CÁLCULO</span><h1>{projectContext ? 'Modelo de impacto' : 'Editor visual'}</h1><p>{projectContext ? (version?.version_number ? `Versión ${version.version_number}` : 'Configuración del impacto') : `${model?.name || 'Modelo de cálculo'}${version?.version_number ? ` · Versión ${version.version_number}` : ''}`}</p></div>
      <div className="cm-version-settings">
        <label>Frecuencia<select disabled={readOnly || busy} value={frequency} onChange={event => setFrequency(event.target.value)}><option value="">Sin valor por defecto</option>{(catalog?.frequencies || []).map(item => <option key={item} value={item}>{OPTION_LABELS[item] || item}</option>)}</select></label>
      </div>
      <span className={`cm-state ${dirty ? 'is-dirty' : ''}`}>{readOnly ? (projectContext ? 'Cálculo configurado · sólo lectura' : 'Versión publicada · sólo lectura') : busy ? 'Procesando…' : dirty ? 'Cambios sin guardar' : (projectContext ? 'Cambios en edición guardados' : 'Borrador guardado')}</span>
      {version?.status === 'published' && canWrite && <div className="cm-toolbar-actions cm-published-actions">
        <button
          type="button"
          className="cm-primary"
          disabled={resolvingDraft}
          onClick={onEditRequested || createOrContinueDraft}
        >
          {activeDraftVersionId ? <Pencil size={17} /> : <FilePlus2 size={17} />}
          {resolvingDraft ? 'Preparando edición…' : projectContext ? 'Editar cálculo' : activeDraftVersionId ? 'Continuar borrador' : 'Crear nuevo borrador'}
        </button>
      </div>}
      {!readOnly && <div className="cm-toolbar-actions">
        <button type="button" disabled={busy || !dirty} onClick={save}><Save size={17} />{projectContext ? 'Guardar' : 'Guardar borrador'}</button>
        <button type="button" disabled={busy} onClick={validate}><ScanSearch size={17} />{projectContext ? 'Revisar' : 'Revisar modelo'}</button>
        <button type="button" className="cm-primary" disabled={busy} onClick={publish}><Send size={17} />{projectContext ? 'Publicar cambios' : 'Publicar versión'}</button>
      </div>}
      {pendingAssociationVersion && <div className="cm-toolbar-actions"><button type="button" disabled={busy} onClick={retryAssociation}><Link size={17} />Reintentar asociación</button></div>}
    </header>
    <CalculationEditor
      nodes={nodes}
      edges={edges}
      catalog={catalog}
      sourceCatalogs={sourceCatalogs}
      sourceScope={sourceScope}
      selectedNodeId={selectedNodeId}
      onSelectionChange={setSelectedNodeId}
      onChange={(nextNodes, nextEdges) => { setNodes(nextNodes); setEdges(nextEdges); setValidation(null); }}
      validation={validation}
      focusRequest={focusRequest}
      readOnly={readOnly || busy}
    />
  </div>;
}

export default function ModelEditorPage() {
  return <CalculationModelWorkspace />;
}
